import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import type { IntakeLoad } from "@/features/intake/api/types"
import { decisionTone, stageTone } from "@/features/intake/lib/intake-status"
import { formatDate } from "@/lib/format"

interface LoadSummaryHeaderProps {
  load: IntakeLoad
}

export function LoadSummaryHeader({ load }: LoadSummaryHeaderProps) {
  const { t } = useTranslation("intake")

  const cooperative = load.Orders?.groupId?.name ?? ""
  const slip = load.DeliveryForms?.billOfLadingNumber ?? ""
  const contract = load.ExporterOrders?.contractNumber ?? null
  const client = load.ExporterOrders?.clientName ?? null

  const eyebrow = [
    cooperative,
    slip ? t("detail.slipLabel", { slip }) : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-xs uppercase tracking-wide">
        {eyebrow}
      </div>
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">
          {load.loadNumber
            ? t("detail.loadHeading", { number: load.loadNumber })
            : t("detail.noNumber")}
        </h1>
        {load.decision ? (
          <StatusBadge tone={decisionTone(load.decision)}>
            {t(`loadDecision.${load.decision}`)}
          </StatusBadge>
        ) : (
          <StatusBadge tone={stageTone(load.status)}>
            {t(`status.${load.status}`)}
          </StatusBadge>
        )}
      </div>
      <div className="text-muted-foreground text-sm">
        {[
          contract ? t("detail.contract", { number: contract }) : null,
          client ? t("detail.client", { name: client }) : null,
          load.arrivedAt
            ? t("detail.arrivedAt", { time: formatDate(load.arrivedAt) })
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </div>
  )
}
